import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth-store';
import { useThemeStore } from '@/stores/theme-store';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { FormField, useFieldValidation, validators } from '@/components/ui/form-field';
import { cn } from '@/lib/utils';
import { Loader2, Sparkles, Sun, Moon, User, Mail, Lock } from 'lucide-react';
import { PasswordStrengthMeter } from '@/components/ui/password-strength';

export function RegisterPage() {
  useDocumentTitle('Sign Up');

  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [nameFocused, setNameFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // Inline validation
  const nameValidation = useFieldValidation([validators.required('Name is required')]);
  const emailValidation = useFieldValidation([validators.required(), validators.email()]);
  const passwordValidation = useFieldValidation([validators.required(), validators.minLength(6, 'Must be at least 6 characters')]);
  const { register, isLoading, user, isAuthenticated } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      toast({
        title: t('auth.welcomeAboard'),
        description: t('auth.accountCreatedAs', { name: user.name || user.email }),
        variant: 'success',
      });
    }
  }, [isAuthenticated, user, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await register(email, password, name);
      navigate('/');
    } catch (err: any) {
      const message = err?.message || t('auth.registrationFailed');
      setError(message);
      toast({ title: t('auth.signUpFailed'), description: message, variant: 'destructive' });
    }
  };

  return (
    <div className="relative min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4 overflow-x-hidden overflow-y-auto">
      {/* ===== Animated Background ===== */}

      {/* Floating gradient orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Orb 1 — large green/teal */}
        <div
          className="absolute -top-32 -left-32 w-[350px] sm:w-[500px] h-[350px] sm:h-[500px] rounded-full opacity-[0.06] dark:opacity-[0.08] animate-float-slow"
          style={{
            background: 'radial-gradient(circle, #10b981 0%, transparent 70%)',
            animationDelay: '0s',
          }}
        />
        {/* Orb 2 — medium blue/purple */}
        <div
          className="absolute top-1/3 -right-40 w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] rounded-full opacity-[0.05] dark:opacity-[0.07] animate-float"
          style={{
            background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)',
            animationDelay: '-2s',
          }}
        />
        {/* Orb 3 — small amber/pink */}
        <div
          className="absolute -bottom-20 left-1/4 sm:left-1/3 w-[250px] sm:w-[350px] h-[250px] sm:h-[350px] rounded-full opacity-[0.04] dark:opacity-[0.06] animate-float-slow"
          style={{
            background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)',
            animationDelay: '-4s',
          }}
        />
        {/* Orb 4 — tiny cyan accent */}
        <div
          className="absolute top-2/3 left-1/4 w-[150px] sm:w-[200px] h-[150px] sm:h-[200px] rounded-full opacity-[0.03] dark:opacity-[0.05] animate-float"
          style={{
            background: 'radial-gradient(circle, #06b6d4 0%, transparent 70%)',
            animationDelay: '-6s',
          }}
        />
        {/* Decorative grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.015] dark:opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='60' height='60' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 60 0 L 0 0 0 60' fill='none' stroke='%2310b981' stroke-width='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Theme toggle — floating top-right */}
      <div className="fixed top-3 right-3 sm:top-4 sm:right-4 z-20 animate-stagger-in animate-stagger-in-delay-1">
        <button
          onClick={toggleTheme}
          className="p-2.5 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-dyndev-green/30 transition-all duration-300 hover:shadow-md hover:shadow-dyndev-green/5 group"
          title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
        >
          <div className="relative w-5 h-5">
            <Sun
              size={20}
              className={`absolute inset-0 transition-all duration-500 ${
                theme === 'dark'
                  ? 'opacity-100 rotate-0 scale-100'
                  : 'opacity-0 rotate-90 scale-75'
              }`}
            />
            <Moon
              size={20}
              className={`absolute inset-0 transition-all duration-500 ${
                theme === 'light'
                  ? 'opacity-100 rotate-0 scale-100'
                  : 'opacity-0 -rotate-90 scale-75'
              }`}
            />
          </div>
          <span className="sr-only">{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
        </button>
      </div>

      {/* Noise overlay — subtle animated grain */}
      <div className="bg-noise opacity-[0.015] pointer-events-none fixed inset-0 animate-[noise-shift_0.5s_steps(5)_infinite]" />

      {/* ===== Content ===== */}
      <div className="w-full max-w-md relative z-10 py-8">
        {/* Logo — with shimmer */}
        <div className="text-center mb-8 animate-stagger-in animate-stagger-in-delay-1">
          <h1 className="text-4xl font-bold tracking-tight mb-2 relative inline-block group">
            <span className="relative">
              Dyn<span className="text-dyndev-green relative">
                Dev
                <span className="absolute inset-0 logo-shimmer rounded" />
              </span>
            </span>
            {/* Subtle sparkle icon that appears on hover */}
            <Sparkles
              size={16}
              className="absolute -top-2 -right-7 text-dyndev-green/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            />
          </h1>
          <p className="text-[var(--text-secondary)] text-sm">{t('app.tagline')}</p>
        </div>

        {/* Register Card — with staggered entrance */}
        <Card className="shadow-xl animate-stagger-in animate-stagger-in-delay-2 relative overflow-hidden">
          {/* Card shimmer border line on hover */}
          <div
            className="absolute inset-0 rounded-xl opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, transparent 40%, rgba(16,185,129,0.08) 50%, transparent 60%)',
              backgroundSize: '200% 200%',
              animation: 'shimmer 3s ease-in-out infinite',
            }}
          />
          <CardHeader>
            <CardTitle className="text-xl">{t('auth.createAccount')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error message */}
              {error && (
                <div className="animate-stagger-in animate-stagger-in-delay-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Name field with inline validation */}
              <FormField
                label={
                  <span className="flex items-center gap-1.5">
                    <User size={12} className="text-[var(--text-secondary)]" />
                    {t('auth.fullName')}
                  </span>
                }
                error={nameValidation.error}
                touched={nameValidation.touched}
                dirty={nameValidation.dirty}
                showError={nameValidation.showError}
                isValid={nameValidation.isValid}
                className="animate-stagger-in animate-stagger-in-delay-3"
              >
                <div className={`relative rounded-lg transition-all duration-300 ${nameFocused ? 'ring-2 ring-dyndev-green/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : nameValidation.showError ? 'ring-2 ring-red-400/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : ''}`}>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      nameValidation.onChange(e);
                    }}
                    onBlur={() => {
                      setNameFocused(false);
                      nameValidation.onBlur();
                    }}
                    onFocus={() => setNameFocused(true)}
                    placeholder={t('auth.namePlaceholder')}
                    required
                    className={cn(
                      'transition-all duration-300',
                      nameValidation.showError && 'border-red-400/50 focus:border-red-400 focus:ring-red-400',
                      nameValidation.isValid && nameValidation.dirty && 'border-dyndev-green/50',
                    )}
                  />
                  <div
                    className={`absolute bottom-0 left-1/2 h-[2px] rounded-full transition-all duration-500 -translate-x-1/2 ${
                      nameValidation.showError ? 'w-3/4 opacity-100 bg-red-400' :
                      nameValidation.isValid && nameValidation.dirty ? 'w-3/4 opacity-100 bg-dyndev-green' :
                      'w-0 opacity-0'
                    }`}
                  />
                </div>
              </FormField>

              {/* Email field with inline validation */}
              <FormField
                label={
                  <span className="flex items-center gap-1.5">
                    <Mail size={12} className="text-[var(--text-secondary)]" />
                    {t('auth.email')}
                  </span>
                }
                error={emailValidation.error}
                touched={emailValidation.touched}
                dirty={emailValidation.dirty}
                showError={emailValidation.showError}
                isValid={emailValidation.isValid}
                className="animate-stagger-in animate-stagger-in-delay-4"
              >
                <div className={`relative rounded-lg transition-all duration-300 ${emailFocused ? 'ring-2 ring-dyndev-green/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : emailValidation.showError ? 'ring-2 ring-red-400/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : ''}`}>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      emailValidation.onChange(e);
                    }}
                    onBlur={() => {
                      setEmailFocused(false);
                      emailValidation.onBlur();
                    }}
                    onFocus={() => setEmailFocused(true)}
                    placeholder={t('auth.emailPlaceholder')}
                    required
                    className={cn(
                      'transition-all duration-300',
                      emailValidation.showError && 'border-red-400/50 focus:border-red-400 focus:ring-red-400',
                      emailValidation.isValid && emailValidation.dirty && 'border-dyndev-green/50',
                    )}
                  />
                  <div
                    className={`absolute bottom-0 left-1/2 h-[2px] rounded-full transition-all duration-500 -translate-x-1/2 ${
                      emailValidation.showError ? 'w-3/4 opacity-100 bg-red-400' :
                      emailValidation.isValid && emailValidation.dirty ? 'w-3/4 opacity-100 bg-dyndev-green' :
                      'w-0 opacity-0'
                    }`}
                  />
                </div>
              </FormField>

              {/* Password field with inline validation */}
              <FormField
                label={
                  <span className="flex items-center gap-1.5">
                    <Lock size={12} className="text-[var(--text-secondary)]" />
                    {t('auth.password')}
                  </span>
                }
                error={passwordValidation.error}
                touched={passwordValidation.touched}
                dirty={passwordValidation.dirty}
                showError={passwordValidation.showError}
                isValid={passwordValidation.isValid}
                className="animate-stagger-in animate-stagger-in-delay-5"
              >
                <div className={`relative rounded-lg transition-all duration-300 ${passwordFocused ? 'ring-2 ring-dyndev-green/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : passwordValidation.showError ? 'ring-2 ring-red-400/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : ''}`}>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      passwordValidation.onChange(e);
                    }}
                    onBlur={() => {
                      setPasswordFocused(false);
                      passwordValidation.onBlur();
                    }}
                    onFocus={() => setPasswordFocused(true)}
                    placeholder={t('auth.passwordPlaceholder')}
                    minLength={6}
                    required
                    className={cn(
                      'transition-all duration-300',
                      passwordValidation.showError && 'border-red-400/50 focus:border-red-400 focus:ring-red-400',
                      passwordValidation.isValid && passwordValidation.dirty && 'border-dyndev-green/50',
                    )}
                  />
                  <div
                    className={`absolute bottom-0 left-1/2 h-[2px] rounded-full transition-all duration-500 -translate-x-1/2 ${
                      passwordValidation.showError ? 'w-3/4 opacity-100 bg-red-400' :
                      passwordValidation.isValid && passwordValidation.dirty ? 'w-3/4 opacity-100 bg-dyndev-green' :
                      'w-0 opacity-0'
                    }`}
                  />
                </div>
              </FormField>
              <div className="animate-stagger-in animate-stagger-in-delay-6">
                <PasswordStrengthMeter password={password} />
              </div>

              {/* Submit button */}
              <div className="animate-stagger-in animate-stagger-in-delay-6">
                <Button
                  type="submit"
                  disabled={isLoading || !name.trim() || !email.trim() || !password.trim()}
                  size="pill"
                  className="w-full group relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-dyndev-green/20 active:scale-[0.98]"
                >
                  {/* Button background shimmer on hover */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 2s ease-in-out infinite',
                    }}
                  />
                  {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
                  {!isLoading && (
                    <span className="ml-2 opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-2 group-hover:translate-x-0 inline-block">
                      →
                    </span>
                  )}
                </Button>
              </div>
            </form>

            {/* Google Sign Up */}
            {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
              <>
                <div className="flex items-center gap-3 my-6 animate-stagger-in animate-stagger-in-delay-7">
                  <Separator className="flex-1" />
                  <span className="text-sm text-[var(--text-secondary)]">{t('auth.or')}</span>
                  <Separator className="flex-1" />
                </div>

                <div className="animate-stagger-in animate-stagger-in-delay-7">
                  <Button variant="outline" className="w-full gap-3 transition-all duration-300 hover:border-dyndev-green/30 hover:shadow-md" asChild>
                    <a href="/client/api/auth/google">
                      <svg width="20" height="20" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      {t('auth.continueWithGoogle')}
                    </a>
                  </Button>
                </div>
              </>
            )}

            {/* Links */}
            <div className="animate-stagger-in animate-stagger-in-delay-8">
              <p className="text-center mt-6 text-sm text-[var(--text-secondary)]">
                {t('auth.hasAccount')}{' '}
                <Link
                  to="/login"
                  className="text-dyndev-green hover:text-dyndev-green/80 transition-all duration-200 font-medium relative group"
                >
                  {t('auth.signIn')}
                  <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-dyndev-green group-hover:w-full transition-all duration-300" />
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center mt-6 text-xs text-[var(--text-secondary)] animate-stagger-in animate-stagger-in-delay-8">
          &copy; {new Date().getFullYear()} DynDev. {t('app.copyright')}
        </p>
      </div>
    </div>
  );
}
